import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  RequiredAsterisk,
  OptionalTag,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { EmojiPicker } from '@/components/ui/emoji-picker';
import { TagInput } from '@/components/ui/tag-input';
import { CurrencySelect } from '@/components/ui/currency-select';
import { useAppSelector, useAppDispatch } from '@/features/hooks';
import { useCurrency } from '@/hooks/useCurrency';
import { useToast } from '@/hooks/use-toast';
import { createTemplate, updateTemplate } from '@/features/templates/templatesSlice';
import type { TransactionTemplate } from '@/lib/types';
import api from '@/services/api';

const templateFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  type: z.enum(['income', 'expense']).default('expense'),
  emoji: z.string().optional(),
  amount: z.coerce
    .number()
    .positive('Amount must be positive')
    .optional()
    .or(z.literal(''))
    .transform((v) => (v === '' ? undefined : v)),
  description: z.string().max(200).optional().or(z.literal('')),
  accountId: z.string().optional().or(z.literal('')),
  categoryId: z.string().optional().or(z.literal('')),
  currency: z.string().length(3).optional().or(z.literal('')),
  tags: z.array(z.string().max(50)).max(10).optional().default([]),
  merchant: z.string().max(100).optional().or(z.literal('')),
  notes: z.string().max(500).optional().or(z.literal('')),
  needsReview: z.boolean().default(false),
});

export type TemplateFormData = z.infer<typeof templateFormSchema>;

interface TemplateFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template?: TransactionTemplate | null;
  initialValues?: Partial<TemplateFormData>;
  onSuccess?: (template: TransactionTemplate) => void;
}

export function TemplateForm({
  open,
  onOpenChange,
  template,
  initialValues,
  onSuccess,
}: TemplateFormProps) {
  const isEditing = !!template;
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const { currency: userCurrency } = useCurrency();
  const accounts = useAppSelector((state) => state.accounts.accounts);
  const categories = useAppSelector((state) => state.categories.categories);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);

  const form = useForm<TemplateFormData>({
    resolver: zodResolver(templateFormSchema),
    defaultValues: {
      name: '',
      type: 'expense',
      emoji: '',
      amount: '' as unknown as undefined,
      description: '',
      accountId: '',
      categoryId: '',
      currency: userCurrency,
      tags: [],
      merchant: '',
      notes: '',
      needsReview: false,
    },
  });

  useEffect(() => {
    if (!open) return;
    api
      .get('/transactions/tags')
      .then((res) => {
        setTagSuggestions((res.data as { data?: string[] })?.data ?? []);
      })
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (template) {
      form.reset({
        name: template.name,
        type: template.type || 'expense',
        emoji: template.emoji || '',
        amount: template.amount ?? ('' as unknown as undefined),
        description: template.description || '',
        accountId:
          typeof template.account === 'object' && template.account !== null
            ? template.account._id
            : template.account || '',
        categoryId:
          typeof template.category === 'object' && template.category !== null
            ? template.category._id
            : template.category || '',
        currency: template.currency || userCurrency,
        tags: template.tags || [],
        merchant: template.merchant || '',
        notes: template.notes || '',
        needsReview: template.needsReview ?? false,
      });
    } else if (initialValues) {
      form.reset({
        name: initialValues.name || '',
        type: initialValues.type || 'expense',
        emoji: initialValues.emoji || '',
        amount: initialValues.amount ?? ('' as unknown as undefined),
        description: initialValues.description || '',
        accountId: initialValues.accountId || '',
        categoryId: initialValues.categoryId || '',
        currency: initialValues.currency || userCurrency,
        tags: initialValues.tags || [],
        merchant: initialValues.merchant || '',
        notes: initialValues.notes || '',
        needsReview: initialValues.needsReview ?? false,
      });
    } else {
      form.reset({
        name: '',
        type: 'expense',
        emoji: '',
        amount: '' as unknown as undefined,
        description: '',
        accountId: '',
        categoryId: '',
        currency: userCurrency,
        tags: [],
        merchant: '',
        notes: '',
        needsReview: false,
      });
    }
  }, [template, initialValues, form, open, userCurrency]);

  const handleSubmit = async (values: TemplateFormData) => {
    setIsSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        name: values.name,
        type: values.type,
        emoji: values.emoji || undefined,
        amount: values.amount || undefined,
        description: values.description || undefined,
        account: values.accountId || undefined,
        category: values.categoryId || undefined,
        currency: values.currency || undefined,
        tags: values.tags?.length ? values.tags : undefined,
        merchant: values.merchant || undefined,
        notes: values.notes || undefined,
        needsReview: values.needsReview,
      };

      let result: TransactionTemplate;
      if (isEditing) {
        const action = await dispatch(
          updateTemplate({ id: template._id, data: payload })
        );
        if (updateTemplate.rejected.match(action)) {
          throw new Error(action.payload as string);
        }
        result = action.payload as TransactionTemplate;
        toast({ title: 'Template updated', description: `"${result.name}" has been updated.` });
      } else {
        const action = await dispatch(createTemplate(payload));
        if (createTemplate.rejected.match(action)) {
          throw new Error(action.payload as string);
        }
        result = action.payload as TransactionTemplate;
        toast({ title: 'Template created', description: `"${result.name}" has been created.` });
      }

      onSuccess?.(result);
      onOpenChange(false);
      form.reset();
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Something went wrong.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="sm:max-w-lg overflow-y-auto"
        data-testid="template-form-sheet"
      >
        <SheetHeader>
          <SheetTitle>{isEditing ? 'Edit Template' : 'Create Template'}</SheetTitle>
          <SheetDescription>
            {isEditing
              ? 'Update template details.'
              : 'Create a reusable transaction template.'}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {/* Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Template Name
                    <RequiredAsterisk />
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. Monthly Rent, Coffee"
                      data-testid="template-form-name"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Type */}
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger data-testid="template-form-type">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="expense">Expense</SelectItem>
                      <SelectItem value="income">Income</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Emoji */}
            <FormField
              control={form.control}
              name="emoji"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Emoji
                    <OptionalTag />
                  </FormLabel>
                  <FormControl>
                    <EmojiPicker value={field.value || ''} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Amount + Currency */}
            <div className="grid gap-3 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Default Amount
                      <OptionalTag />
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Default amount (optional)"
                        data-testid="template-form-amount"
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Currency
                      <OptionalTag />
                    </FormLabel>
                    <FormControl>
                      <CurrencySelect
                        value={field.value || ''}
                        onChange={field.onChange}
                        required
                        testId="currency-select-template"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Description
                    <OptionalTag />
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Brief description"
                      maxLength={200}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Account */}
            <FormField
              control={form.control}
              name="accountId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Account
                    <OptionalTag />
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || ''}
                    defaultValue={field.value || ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select account" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {accounts.map((acc) => (
                        <SelectItem key={acc.id} value={acc.id}>
                          {acc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Category */}
            <FormField
              control={form.control}
              name="categoryId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Category
                    <OptionalTag />
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || ''}
                    defaultValue={field.value || ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.icon ? `${cat.icon} ` : ''}
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Tags */}
            <FormField
              control={form.control}
              name="tags"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Tags
                    <OptionalTag />
                  </FormLabel>
                  <TagInput
                    value={(field.value as string[]) ?? []}
                    onChange={field.onChange}
                    suggestions={tagSuggestions}
                    placeholder="Add tags..."
                    maxTags={10}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Merchant */}
            <FormField
              control={form.control}
              name="merchant"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Merchant
                    <OptionalTag />
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Merchant name"
                      maxLength={100}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Notes */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Notes
                    <OptionalTag />
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Additional notes..."
                      rows={3}
                      maxLength={500}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Needs Review */}
            <FormField
              control={form.control}
              name="needsReview"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center gap-2 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => field.onChange(!!checked)}
                    />
                  </FormControl>
                  <FormLabel className="text-sm font-normal">
                    Mark transactions for review when using this template
                  </FormLabel>
                </FormItem>
              )}
            />

            <SheetFooter className="flex flex-row gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1 min-h-[44px]"
                onClick={() => onOpenChange(false)}
                data-testid="template-form-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 min-h-[44px]"
                disabled={isSubmitting}
                data-testid="template-form-submit"
              >
                {isSubmitting
                  ? 'Saving...'
                  : isEditing
                    ? 'Save Changes'
                    : 'Create Template'}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
